import { App, Button, Form, Spin } from "antd";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { RcFile } from "antd/es/upload";
import { urlToUploadFile } from "../../../../utils/Converte";
import {
  useGetProgramById,
  useUpdateProgramByUpdateProgramAdminRequest,
} from "./model";
import { UploadFile } from "antd/lib";
import { ProgramOutline } from "../create/ui/programOutline";
import { ProgramBookingSetting } from "../create/ui/programBookingSetting";
import { ProgramTicketSetting } from "../create/ui/programTicketSetting";
import { ProgramOptionSetting } from "../create/ui/programOptionSetting";
import { useQueryClient } from "@tanstack/react-query";

interface IFormOutlineData {
  id: number;
  bookingPeriod: [string, string];
  description: string;
  detailHtml: string;
  detailImageUrlList: UploadFile[];
  isDisplay: boolean;
  isOnline: boolean;
  isPaid: boolean;
  isAutoApprovalEnabled: boolean;
  locationName: string;
  notificationHtml: string;
  refundNotiHtml: string;
  onlineUrl?: string;
  programPeriod: [string, string];
  roadNameAddress: IRoadNameAddress;
  thumbnailImageUrl: [UploadFile];
  title: string;
}

const ProgramUpdate = () => {
  const id = Number(useParams().id);
  const { message } = App.useApp();
  const [programEventOutlineList, setProgramEventOutlineList] = useState<
    IProgramEventOutline[]
  >([]);
  const [programTicketList, setProgramTicketList] = useState<IProgramTicket[]>(
    []
  );
  const [programSelectOptionList, setProgramSelectOptionList] = useState<
    IProgramSelectOption[]
  >([]);
  const [programSubjectOptionList, setProgramSubjectOptionList] = useState<
    IProgramSubjectOption[]
  >([]);
  const [isPaidState, setIsPaidState] = useState<boolean>(true);
  const navigate = useNavigate();
  const { data, isFetching, isError } = useGetProgramById(id);
  const queryClient = useQueryClient();
  const updateProgramByUpdateProgramAdminRequest =
    useUpdateProgramByUpdateProgramAdminRequest();

  const onFinish = (value: IFormOutlineData) => {
    const updateProgramAdminRequest: IUpdateProgramAdminRequest = {
      id: value.id,
      isDisplay: value.isDisplay,
      title: value.title,
      description: value.description,
      isPaid: value.isPaid,
      isAutoApprovalEnabled: value.isAutoApprovalEnabled,
      isOnline: value.isOnline,
      onlineUrl: value.onlineUrl,
      roadNameAddress: value.roadNameAddress,
      locationName: value.locationName,
      detailHtml: value.detailHtml,
      notificationHtml: value.notificationHtml,
      refundNotiHtml: value.refundNotiHtml,
      programPeriod: value.programPeriod,
      bookingPeriod: value.bookingPeriod,
      thumbnailImageFile: value.thumbnailImageUrl[0]?.originFileObj,
      thumbnailImageUrl: value.thumbnailImageUrl[0]?.url,
      detailImageFileList: value.detailImageUrlList
        .filter((q) => q.originFileObj)
        ?.map((q) => q.originFileObj),
      detailImageUrlList: value.detailImageUrlList
        .filter((q) => q.url)
        .map((q) => q.url),
      programEventOutlineList:
        programEventOutlineList?.map((q) => ({
          ...q,
          id: typeof q.id === "number" ? undefined : q.id,
          programEventList: q.programEventList.map((w) => ({
            ...w,
            id: typeof w.id === "number" ? undefined : w.id,
          })),
        })) ?? [],
      programTicketList:
        programTicketList?.map((q) => ({
          ...q,
          id: typeof q.id === "number" ? undefined : q.id,
        })) ?? [],
      programSelectOptionList:
        programSelectOptionList?.map((q) => ({
          ...q,
          id: typeof q.id === "number" ? undefined : q.id,
        })) ?? [],
      programSubjectOptionList:
        programSubjectOptionList?.map((q) => ({
          ...q,
          id: typeof q.id === "number" ? undefined : q.id,
        })) ?? [],
    };
    if (
      (updateProgramAdminRequest?.programEventOutlineList?.[0]?.programEventList
        ?.length ?? 0) <= 0
    ) {
      message.error("예약설정 중 회차를 추가해주세요");
    } else if (
      updateProgramAdminRequest?.isPaid &&
      (updateProgramAdminRequest?.programTicketList?.length ?? 0) <= 0
    ) {
      message.error("티켓을 추가해주세요");
    } else {
      updateProgramByUpdateProgramAdminRequest.mutate(
        updateProgramAdminRequest,
        {
          onSuccess: (res) => {
            if (!res.data.errors) {
              queryClient.invalidateQueries({
                queryKey: ["getProgramById", id],
              });
              navigate("/wellness-program/program");
            } else {
              message.error(
                "에러가 발생했습니다 개발팀에 문의해주세요  " +
                res.data.errors[0].message
              );
            }
          },
        }
      );
    }
  };

  useEffect(() => {
    if (!data) return;
    setProgramEventOutlineList(data?.programEventOutlineList);
    setProgramTicketList(data?.programTicketList);
    setProgramSelectOptionList(data?.programSelectOptionList);
    setProgramSubjectOptionList(data?.programSubjectOptionList);
  }, [isFetching]);

  if (isNaN(id) || isError) {
    isError && message.error("데이터를 불러올 수 없습니다.");
    isNaN(id) && message.error("id가 없거나 숫자가 아닙니다");
    return <Navigate to={`/wellness-program/program`} replace />;
  }
  return (
    <>
      {isFetching ? (
        <Spin size="large" />
      ) : (
        <Form
          labelCol={{ span: 3 }}
          labelAlign="left"
          colon={false}
          onFinish={onFinish}
          initialValues={{
            id: data?.id,
            isDisplay: data?.isDisplay,
            title: data?.title,
            description: data?.description,
            isBooking: data?.isBooking,
            isPaid: data?.isPaid,
            isAutoApprovalEnabled: data?.isAutoApprovalEnabled,
            isOnline: data?.isOnline,
            onlineUrl: data?.onlineUrl,
            roadNameAddress: {
              address: data?.roadNameAddress,
              lat: data?.lat,
              lon: data?.lon,
            },
            locationName: data?.locationName,
            detailHtml: data?.detailHtml,
            notificationHtml: data?.notificationHtml,
            refundNotiHtml: data?.refundNotiHtml,
            programPeriod: data?.programPeriod,
            bookingPeriod: data?.bookingPeriod,
            thumbnailImageUrl: data?.thumbnailImageUrl
              ? [
                typeof data.thumbnailImageUrl === "string"
                  ? urlToUploadFile(data.thumbnailImageUrl)
                  : data.thumbnailImageUrl,
              ]
              : undefined,
            detailImageUrlList: data?.detailImageUrlList?.map(
              (url: string | RcFile) =>
                typeof url === "string" ? urlToUploadFile(url) : url
            ),
          }}
        >
          <Form.Item name={"id"} noStyle></Form.Item>

          <ProgramOutline isDefalutOpen setIsPaidState={setIsPaidState} />

          <div style={{ position: "relative", height: "24px" }}>
            <div
              style={{
                position: "absolute",
                left: "-20px",
                width: "calc(100% + 40px)",
                height: "24px",
                backgroundColor: "var(--ant-layout-footer-bg)",
              }}
            />
          </div>

          <ProgramBookingSetting
            key={
              "ProgramBookingSetting" + JSON.stringify(programEventOutlineList)
            }
            initBookingPeriod={data?.bookingPeriod}
            initProgramPeriod={data?.programPeriod}
            programEventOutlineList={programEventOutlineList}
            setProgramEventOutlineList={setProgramEventOutlineList}
            isDefalutOpen
          />

          <div style={{ position: "relative", height: "24px" }}>
            <div
              style={{
                position: "absolute",
                left: "-20px",
                width: "calc(100% + 40px)",
                height: "24px",
                backgroundColor: "var(--ant-layout-footer-bg)",
              }}
            />
          </div>

          {isPaidState && (
            <ProgramTicketSetting
              key={"ProgramTicketSetting" + JSON.stringify(programTicketList)}
              initProgramTicketList={programTicketList}
              setProgramTicketList={setProgramTicketList}
              isDefalutOpen
            />
          )}

          <div style={{ position: "relative", height: "24px" }}>
            <div
              style={{
                position: "absolute",
                left: "-20px",
                width: "calc(100% + 40px)",
                height: "24px",
                backgroundColor: "var(--ant-layout-footer-bg)",
              }}
            />
          </div>

          <ProgramOptionSetting
            key={
              "ProgramOptionSetting" +
              JSON.stringify(
                programSelectOptionList?.toString() +
                programSubjectOptionList?.toString()
              )
            }
            initProgramSelectOptionList={programSelectOptionList}
            setProgramSelectOptionList={setProgramSelectOptionList}
            initProgramSubjectOptionList={programSubjectOptionList}
            setProgramSubjectOptionList={setProgramSubjectOptionList}
            isDefalutOpen
          />

          <div style={{ position: "relative", height: "24px" }}>
            <div
              style={{
                position: "absolute",
                left: "-20px",
                width: "calc(100% + 40px)",
                height: "24px",
                backgroundColor: "var(--ant-layout-footer-bg)",
              }}
            />
          </div>

          <div
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              margin: "335px 0 -55px -40px",
              padding: "20px 40px",
              display: "flex",
              justifyContent: "space-between",
              width: "calc(100% + 80px)",
            }}
          >
            <Button
              loading={updateProgramByUpdateProgramAdminRequest.isPending}
              onClick={() => navigate("/wellness-program/program")}
            >
              취소
            </Button>
            <Button
              loading={updateProgramByUpdateProgramAdminRequest.isPending}
              htmlType="submit"
              type="primary"
            >
              수정
            </Button>
          </div>
        </Form>
      )}
    </>
  );
};

export { ProgramUpdate };
